import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  BookIcon,
  BoxIcon,
  Button,
  CalendarIcon,
  CameraIcon,
  CheckIcon,
  FileIcon,
  FileTextIcon,
  IdCardIcon,
  InfoIcon,
  LockIcon,
  MicrochipIcon,
  Modal,
  PawIcon,
  PencilIcon,
  RotateLeftIcon,
  ScaleIcon,
  ScissorsIcon,
  SwapIcon,
  TagIcon,
  UserIcon,
  VenusIcon,
  XIcon,
} from "@fawkes/ui";
import type { ReactNode } from "react";

// Tile field-visibility migration: the animal-detail per-tile config affordances — the Jmix pencil
// dialogs (per-user field visibility, AnimalDetailView.openBasisFieldsDialog/openFeedingSectionsDialog/
// openWeightTileDialog) and the lock dialog (org-wide foster access policy,
// AnimalDetailView.openFosterVisibilityDialog). The pencil preferences persist per user via
// GET/PUT /api/animals/field-preferences; the lock dialog edits the organization-wide singleton via
// GET/PUT /api/foster-policy (ROLE_FOSTER_POLICY_EDIT).

// ---- per-user field preferences ---------------------------------------------------------------------

export interface FieldPreference {
  fieldKey: string;
  visible: boolean;
}

/**
 * The current user's animal field preferences as a hidden-key set (namespaced keys: `basis.naam` …
 * `gewichten.chart`). useCustom caches by url, so the tiles and dialogs share one fetch.
 */
export function useAnimalFieldPreferences() {
  const { data, refetch } = useCustom({ url: "animals/field-preferences", method: "get" });
  const prefs = (data?.data as FieldPreference[] | undefined) ?? [];
  const hidden = new Set(prefs.filter((p) => !p.visible).map((p) => p.fieldKey));
  return { hidden, loaded: data !== undefined, refetch };
}

// ---- the pencil dialog (checkbox list, Jmix openBasisFieldsDialog shape) ------------------------------

export interface FieldOption {
  /** The full namespaced preference key (`basis.naam`). */
  key: string;
  label: string;
}

/**
 * A per-tile field-visibility dialog: one checkbox per field, checked = visible (the Jmix
 * Basisinformatie-velden / Voeding-secties / Gewichten-tegel dialogs). Saving PUTs the tile's keys to
 * /api/animals/field-preferences and reports back via onSaved.
 */
export function FieldVisibilityDialog({ open, title, fields, hidden, onClose, onSaved }: {
  open: boolean;
  title: string;
  fields: FieldOption[];
  hidden: Set<string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: saving } = useCustomMutation();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      setChecked(new Set(fields.filter((f) => !hidden.has(f.key)).map((f) => f.key)));
      setError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = () => {
    setError(false);
    mutate(
      {
        url: "animals/field-preferences",
        method: "put",
        values: { preferences: fields.map((f) => ({ fieldKey: f.key, visible: checked.has(f.key) })) },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => { onSaved(); onClose(); }, onError: () => setError(true) },
    );
  };

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-fieldconfig">
        {/* Sliding switches, not plain checkboxes — the Jmix dialog renders its Checkboxes in the
            switch theme (fui-toggle mirrors that; same markup as the Basis tile's Switch). */}
        {fields.map((f) => (
          <label key={f.key} className="fui-toggle fawkes-fieldconfig__option">
            <input
              type="checkbox" role="switch" className="fui-toggle__input"
              checked={checked.has(f.key)} aria-label={f.label}
              onChange={() => toggle(f.key)}
            />
            <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
            <span className="fawkes-fieldconfig__label">{f.label}</span>
          </label>
        ))}
        {error && <span className="fawkes-error" role="alert">{t("animalDetailView.fieldConfig.saveError")}</span>}
      </div>
    </Modal>
  );
}

// ---- the lock dialog (foster access policy) -----------------------------------------------------------

const LEVELS = ["hidden", "readonly", "editable", "deletable"] as const;
const levelIdx = (level: string) => Math.max(0, (LEVELS as readonly string[]).indexOf(level));
const minLevel = (a: string, b: string) => (levelIdx(a) <= levelIdx(b) ? a : b);

interface PolicyFieldView { level: string; maxLevel: string }
interface PolicyTileView { level: string; maxLevel: string; fields: Record<string, PolicyFieldView> | null }
interface PolicyView { version: number; tiles: Record<string, PolicyTileView> }

function LevelSelect({ value, max, disabled, ariaLabel, onChange, t }: {
  value: string; max: string; disabled: boolean; ariaLabel: string;
  onChange: (level: string) => void; t: (k: string) => string;
}) {
  return (
    <select
      className="fui-field__input fui-select fawkes-foster__level"
      value={minLevel(value, max)}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    >
      {LEVELS.filter((l) => levelIdx(l) <= levelIdx(max)).map((l) => (
        <option key={l} value={l}>{t(`animalDetailView.foster.level.${l}`)}</option>
      ))}
    </select>
  );
}

/** The check/dash reference column: is this tile/field visible in the backoffice? */
function BackofficeMark({ visible, t }: { visible: boolean; t: (k: string) => string }) {
  const title = t(visible ? "animalDetailView.foster.boVisible" : "animalDetailView.foster.boHidden");
  return visible
    ? <span className="fawkes-foster__bo fawkes-foster__bo--on" title={title}><CheckIcon width={14} height={14} /></span>
    : <span className="fawkes-foster__bo" title={title}>—</span>;
}

/**
 * The "Pleeggezin — <tegel>" lock dialog: one tile-level row plus, for the tiles with field-level
 * config (Basisinformatie, Voeding), one row per field — *veldlabel | backoffice-referentie |
 * pleeggezin-niveau*, exactly the Jmix 3-column grid. Level options run up to
 * min(intrinsic max, backoffice visibility); lowering the tile level re-clamps every field select
 * (the cascade). The policy is re-fetched on every open (no client cache) and saved with its
 * version; a stale dialog gets the server's 409 body (current version + policy) and re-seeds.
 */
export function FosterVisibilityDialog({ open, tileKey, tileLabel, backofficeTileVisible, backofficeFields, fieldLabels, onClose }: {
  open: boolean;
  /** Canonical policy tile key (BASIS/INENTINGEN/…). */
  tileKey: string;
  tileLabel: string;
  /** Whether the current user shows this tile in the backoffice (the reference column). */
  backofficeTileVisible: boolean;
  /** Per field: visible in the backoffice for the current user (BASIS/VOEDING only). */
  backofficeFields: Record<string, boolean>;
  /** The field rows, in canonical order (empty for tile-level-only tiles). */
  fieldLabels: FieldOption[];
  onClose: () => void;
}) {
  const t = useTranslate();
  const { data, refetch } = useCustom({
    url: "foster-policy",
    method: "get",
    queryOptions: { enabled: open },
  });
  const { mutate, isLoading: saving } = useCustomMutation();

  const [version, setVersion] = useState(0);
  const [tileLevel, setTileLevel] = useState("hidden");
  const [fieldLevels, setFieldLevels] = useState<Record<string, string>>({});
  const [maxTile, setMaxTile] = useState("hidden");
  const [maxFields, setMaxFields] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<"conflict" | "error" | null>(null);

  // Bare field key inside the policy ("naam"); the preference keys are namespaced ("basis.naam").
  const bareKey = (key: string) => key.substring(key.indexOf(".") + 1);

  const seed = (policy: PolicyView) => {
    const tile = policy.tiles[tileKey];
    if (!tile) return;
    setVersion(policy.version);
    setMaxTile(tile.maxLevel);
    setTileLevel(minLevel(tile.level, backofficeTileVisible ? tile.maxLevel : "hidden"));
    const levels: Record<string, string> = {};
    const maxes: Record<string, string> = {};
    for (const f of fieldLabels) {
      const key = bareKey(f.key);
      const view = tile.fields?.[key];
      maxes[key] = view?.maxLevel ?? "hidden";
      levels[key] = view?.level ?? "hidden";
    }
    setMaxFields(maxes);
    setFieldLevels(levels);
  };

  useEffect(() => {
    if (open && data?.data) {
      seed(data.data as unknown as PolicyView);
      setNotice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data?.data]);

  useEffect(() => {
    if (open) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The effective cap for a field: its intrinsic max, floored to hidden when the backoffice hides
  // it, and never above the tile's current level (the cascade).
  const fieldCap = (key: string) => {
    const boVisible = backofficeFields[key] !== false;
    return minLevel(boVisible ? (maxFields[key] ?? "hidden") : "hidden", tileLevel);
  };

  const changeTileLevel = (level: string) => {
    setTileLevel(level);
    // Re-clamp every field to the new tile level immediately (Jmix repopulateLevelSelect cascade).
    setFieldLevels((prev) => {
      const next: Record<string, string> = {};
      for (const f of fieldLabels) {
        const key = bareKey(f.key);
        const boVisible = backofficeFields[key] !== false;
        const cap = minLevel(boVisible ? (maxFields[key] ?? "hidden") : "hidden", level);
        next[key] = minLevel(prev[key] ?? "hidden", cap);
      }
      return next;
    });
  };

  const save = () => {
    setNotice(null);
    const fields: Record<string, string> = {};
    for (const f of fieldLabels) {
      const key = bareKey(f.key);
      fields[key] = minLevel(fieldLevels[key] ?? "hidden", fieldCap(key));
    }
    mutate(
      {
        url: "foster-policy",
        method: "put",
        values: {
          version,
          tiles: { [tileKey]: { level: tileLevel, ...(fieldLabels.length > 0 ? { fields } : {}) } },
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          const e = err as { statusCode?: number; response?: unknown };
          if (e.statusCode === 409) {
            // The 409 body carries the current version + policy so the dialog can refresh; the
            // simplest faithful refresh is a refetch (same payload as the body).
            setNotice("conflict");
            refetch();
          } else {
            setNotice("error");
          }
        },
      },
    );
  };

  const tileCap = backofficeTileVisible ? maxTile : "hidden";

  return (
    <Modal
      open={open}
      title={t("animalDetailView.foster.title", { args: [tileLabel] })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-foster">
        <p className="fawkes-foster__rule">{t("animalDetailView.foster.rule")}</p>
        <div className="fawkes-foster__grid">
          <span className="fawkes-foster__head" aria-hidden="true"></span>
          <span className="fawkes-foster__head">{t("animalDetailView.foster.colBackoffice")}</span>
          <span className="fawkes-foster__head">{t("animalDetailView.foster.colFoster")}</span>

          <span className="fawkes-foster__tile-label">{t("animalDetailView.foster.tileRow")}</span>
          <BackofficeMark visible={backofficeTileVisible} t={t} />
          <span title={!backofficeTileVisible
            ? t("animalDetailView.foster.tileOffTooltip")
            : tileCap === "hidden" ? t("animalDetailView.foster.notAvailableTooltip") : undefined}>
            <LevelSelect
              value={tileLevel} max={tileCap} disabled={!backofficeTileVisible}
              ariaLabel={t("animalDetailView.foster.tileRow")} onChange={changeTileLevel} t={t}
            />
          </span>

          {fieldLabels.map((f) => {
            const key = bareKey(f.key);
            const boVisible = backofficeFields[key] !== false;
            const cap = fieldCap(key);
            return (
              <div key={f.key} className="fawkes-foster__row" role="presentation">
                <span className={boVisible ? undefined : "fawkes-foster__label--bo-hidden"}>{f.label}</span>
                <BackofficeMark visible={boVisible} t={t} />
                <span title={boVisible ? undefined : t("animalDetailView.foster.boHiddenTooltip")}>
                  <LevelSelect
                    value={fieldLevels[key] ?? "hidden"} max={cap}
                    disabled={!boVisible || cap === "hidden"}
                    ariaLabel={f.label}
                    onChange={(level) => setFieldLevels((prev) => ({ ...prev, [key]: level }))} t={t}
                  />
                </span>
              </div>
            );
          })}
        </div>
        {notice && (
          <span className="fawkes-error" role="alert">
            {t(notice === "conflict" ? "animalDetailView.foster.conflict" : "animalDetailView.foster.saveError")}
          </span>
        )}
      </div>
    </Modal>
  );
}

// ---- the in-tile config bar ---------------------------------------------------------------------------

/**
 * The tile-header affordances of the Jmix screen, rendered as a right-aligned bar at the top of the
 * tile content (the shared TileBoard chrome has no per-tile action slot): a pencil (per-user field
 * visibility — only the tiles with configurable fields) and a lock (foster access policy — only for
 * users with ROLE_FOSTER_POLICY_EDIT). Jmix parity: the host renders this bar only while
 * layout-edit mode is active — the buttons live behind the layout-lock toggle, like on :8080.
 */
export function TileConfigBar({ onEditFields, onOpenFoster }: {
  onEditFields?: () => void;
  onOpenFoster?: () => void;
}) {
  const t = useTranslate();
  if (!onEditFields && !onOpenFoster) return null;
  return (
    <div className="fawkes-tileconfig">
      {onEditFields && (
        <button
          type="button" className="fawkes-tileconfig__btn"
          aria-label={t("animalDetailView.fieldConfig.edit")} title={t("animalDetailView.fieldConfig.edit")}
          onClick={onEditFields}
        >
          <PencilIcon width={15} height={15} />
        </button>
      )}
      {onOpenFoster && (
        <button
          type="button" className="fawkes-tileconfig__btn"
          aria-label={t("animalDetailView.foster.lockTitle")} title={t("animalDetailView.foster.lockTitle")}
          onClick={onOpenFoster}
        >
          <LockIcon width={15} height={15} />
        </button>
      )}
    </div>
  );
}

// ---- het gecombineerde instellingenvenster (de puntjes rechtsboven in een tegel) -----------------------

/** Icoon per veld (kale sleutel, zoals in het pleeggezinbeleid). Onbekende velden krijgen het info-icoon. */
function veldIcoon(bare: string): ReactNode {
  const p = { width: 18, height: 18, "aria-hidden": true } as const;
  switch (bare) {
    case "naam": return <UserIcon {...p} />;
    case "geslacht": return <VenusIcon {...p} />;
    case "gecastreerd": return <ScissorsIcon {...p} />;
    case "castratieDatum":
    case "geboortedatum": return <CalendarIcon {...p} />;
    case "diersoort": return <PawIcon {...p} />;
    case "chipPasnummer": return <MicrochipIcon {...p} />;
    case "paspoortNummer": return <IdCardIcon {...p} />;
    case "stamboomNummer": return <FileTextIcon {...p} />;
    case "overledendatum": return <XIcon {...p} />;
    case "dierVacht": return <TagIcon {...p} />;
    case "kruising": return <SwapIcon {...p} />;
    case "kenmerken": return <FileIcon {...p} />;
    case "foto": return <CameraIcon {...p} />;
    case "schema": return <BoxIcon {...p} />;
    case "log": return <BookIcon {...p} />;
    case "chart": return <ScaleIcon {...p} />;
    default: return <InfoIcon {...p} />;
  }
}

/**
 * "<Tegel> instellen": per veld of je het in de backoffice ziet (per gebruiker, /animals/field-preferences)
 * en wat het pleeggezin ermee mag (organisatiebreed, /foster-policy) — in één venster, zoals het ontwerp.
 * Een veld dat in de backoffice uit staat, is voor het pleeggezin automatisch "Niet zien". Het
 * pleeggezinbeleid staat er alleen in voor wie het mag wijzigen (ROLE_FOSTER_POLICY_EDIT).
 */
export function TegelInstellingenDialog({
  tileKey,
  tileLabel,
  fields,
  hiddenPrefs,
  fosterAllowed,
  backofficeTileVisible,
  onClose,
  onSaved,
}: {
  /** Canonieke beleidssleutel (BASIS/INENTINGEN/…). */
  tileKey: string;
  tileLabel: string;
  /** De velden met een backoffice-schakelaar (namespaced voorkeursleutels); leeg voor tegels zonder. */
  fields: FieldOption[];
  hiddenPrefs: Set<string>;
  fosterAllowed: boolean;
  backofficeTileVisible: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslate();
  const { data, refetch } = useCustom({ url: "foster-policy", method: "get", queryOptions: { enabled: fosterAllowed } });
  const { mutateAsync } = useCustomMutation();
  const bare = (key: string) => key.substring(key.indexOf(".") + 1);

  const [zichtbaar, setZichtbaar] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, !hiddenPrefs.has(f.key)])),
  );
  const [versie, setVersie] = useState(0);
  const [tegelNiveau, setTegelNiveau] = useState("hidden");
  const [tegelMax, setTegelMax] = useState("hidden");
  const [veldNiveau, setVeldNiveau] = useState<Record<string, string>>({});
  const [veldMax, setVeldMax] = useState<Record<string, string>>({});
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<"conflict" | "error" | null>(null);

  useEffect(() => {
    const policy = data?.data as unknown as PolicyView | undefined;
    const tile = policy?.tiles?.[tileKey];
    if (!policy || !tile) return;
    setVersie(policy.version);
    setTegelMax(tile.maxLevel);
    setTegelNiveau(minLevel(tile.level, backofficeTileVisible ? tile.maxLevel : "hidden"));
    const niveaus: Record<string, string> = {};
    const maxen: Record<string, string> = {};
    for (const [k, v] of Object.entries(tile.fields ?? {})) {
      niveaus[k] = v.level;
      maxen[k] = v.maxLevel;
    }
    setVeldNiveau(niveaus);
    setVeldMax(maxen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  const heeftBeleidVeld = (key: string) => fosterAllowed && veldMax[bare(key)] !== undefined;
  const tegelCap = backofficeTileVisible ? tegelMax : "hidden";
  const veldCap = (key: string) =>
    zichtbaar[key] === false ? "hidden" : minLevel(veldMax[bare(key)] ?? "hidden", tegelNiveau);

  const opslaan = async () => {
    setBezig(true);
    setMelding(null);
    try {
      if (fields.length > 0) {
        await mutateAsync({
          url: "animals/field-preferences",
          method: "put",
          values: { preferences: fields.map((f) => ({ fieldKey: f.key, visible: zichtbaar[f.key] !== false })) },
          successNotification: false,
          errorNotification: false,
        });
      }
      if (fosterAllowed && data?.data) {
        const velden: Record<string, string> = {};
        for (const k of Object.keys(veldMax)) {
          const pref = fields.find((f) => bare(f.key) === k);
          const cap = pref ? veldCap(pref.key) : minLevel(veldMax[k], tegelNiveau);
          velden[k] = minLevel(veldNiveau[k] ?? "hidden", cap);
        }
        await mutateAsync({
          url: "foster-policy",
          method: "put",
          values: {
            version: versie,
            tiles: { [tileKey]: { level: tegelNiveau, ...(Object.keys(velden).length > 0 ? { fields: velden } : {}) } },
          },
          successNotification: false,
          errorNotification: false,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      const e = err as { statusCode?: number };
      if (e.statusCode === 409) {
        setMelding("conflict");
        void refetch();
      } else {
        setMelding("error");
      }
    } finally {
      setBezig(false);
    }
  };

  const standaard = () => setZichtbaar(Object.fromEntries(fields.map((f) => [f.key, true])));

  return (
    <Modal
      open
      size="wide"
      title={t("animalDetailView.tileSettings.title", { args: [tileLabel] })}
      onClose={onClose}
      footer={
        <div className="tegel-instellingen__voet">
          {fields.length > 0 ? (
            <Button variant="secondary" onClick={standaard} title={t("animalDetailView.tileSettings.defaultsHint")}>
              <RotateLeftIcon width={15} height={15} /> {t("animalDetailView.tileSettings.defaults")}
            </Button>
          ) : (
            <span />
          )}
          <span className="tegel-instellingen__rechts">
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="primary" onClick={() => void opslaan()} disabled={bezig}>{t("common.save")}</Button>
          </span>
        </div>
      }
    >
      <p className="tegel-instellingen__intro">
        {t(fosterAllowed ? "animalDetailView.tileSettings.intro" : "animalDetailView.tileSettings.introBackoffice")}
      </p>
      {fosterAllowed && fields.length > 0 && (
        <div className="tegel-instellingen__info">
          <InfoIcon width={20} height={20} aria-hidden="true" />
          <div>
            <strong>{t("animalDetailView.tileSettings.ruleTitle")}</strong>
            <span>{t("animalDetailView.tileSettings.ruleText")}</span>
          </div>
        </div>
      )}
      <div className="tegel-instellingen__kader">
      <table className="tegel-instellingen__tabel">
        <thead>
          <tr>
            <th colSpan={2}>{t("animalDetailView.tileSettings.colField")}</th>
            {fields.length > 0 && (
              <th className="is-midden">
                <span className="tegel-instellingen__kolomkop">
                  {t("animalDetailView.tileSettings.colBackoffice")}
                  <span className="tegel-instellingen__uitleg" title={t("animalDetailView.tileSettings.colBackofficeHint")}>
                    <InfoIcon width={14} height={14} aria-label={t("animalDetailView.tileSettings.colBackofficeHint")} />
                  </span>
                </span>
              </th>
            )}
            {fosterAllowed && (
              <th>
                <span className="tegel-instellingen__kolomkop">
                  {t("animalDetailView.tileSettings.colFoster")}
                  <span className="tegel-instellingen__uitleg" title={t("animalDetailView.tileSettings.colFosterHint")}>
                    <InfoIcon width={14} height={14} aria-label={t("animalDetailView.tileSettings.colFosterHint")} />
                  </span>
                </span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {fosterAllowed && (
            <tr className="tegel-instellingen__tegelrij">
              <td className="tegel-instellingen__icoon"><LockIcon width={18} height={18} aria-hidden="true" /></td>
              <td>{t("animalDetailView.tileSettings.wholeTile")}</td>
              {fields.length > 0 && <td className="is-midden">—</td>}
              <td>
                <LevelSelect
                  value={tegelNiveau}
                  max={tegelCap}
                  disabled={!backofficeTileVisible}
                  ariaLabel={`${t("animalDetailView.tileSettings.colFoster")} — ${t("animalDetailView.tileSettings.wholeTile")}`}
                  onChange={setTegelNiveau}
                  t={t}
                />
              </td>
            </tr>
          )}
          {fields.map((f) => {
            const aan = zichtbaar[f.key] !== false;
            return (
              <tr key={f.key} className={aan ? undefined : "is-uit"}>
                <td className="tegel-instellingen__icoon">{veldIcoon(bare(f.key))}</td>
                <td>{f.label}</td>
                <td className="is-midden">
                  <label className="fui-toggle tegel-instellingen__schakelaar">
                    <input
                      type="checkbox"
                      role="switch"
                      className="fui-toggle__input"
                      checked={aan}
                      aria-label={`${t("animalDetailView.tileSettings.colBackoffice")} — ${f.label}`}
                      onChange={(e) => setZichtbaar((z) => ({ ...z, [f.key]: e.target.checked }))}
                    />
                    <span className="fui-toggle__track" aria-hidden="true"><span className="fui-toggle__thumb" /></span>
                  </label>
                </td>
                {fosterAllowed && (
                  <td>
                    {heeftBeleidVeld(f.key) ? (
                      <LevelSelect
                        value={aan ? (veldNiveau[bare(f.key)] ?? "hidden") : "hidden"}
                        max={veldCap(f.key)}
                        disabled={!aan || veldCap(f.key) === "hidden"}
                        ariaLabel={`${t("animalDetailView.tileSettings.colFoster")} — ${f.label}`}
                        onChange={(level) => setVeldNiveau((v) => ({ ...v, [bare(f.key)]: level }))}
                        t={t}
                      />
                    ) : (
                      <span className="tegel-instellingen__nvt">{t("animalDetailView.tileSettings.followsTile")}</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {melding && (
        <span className="fawkes-error" role="alert">
          {t(melding === "conflict" ? "animalDetailView.foster.conflict" : "animalDetailView.foster.saveError")}
        </span>
      )}
    </Modal>
  );
}
